import type { TeamMembersFilterInput } from '../hooks'
import type { CollaborationRole } from './collaborationRoles'

/**
 * `pending` is client-side: invitations come from `pendingInvitations`, not
 * `teamMembers`, so it hides the members instead of reaching the server.
 */
export type TeamMembersStatusFilter = 'all' | 'active' | 'locked' | 'pending'

export type TeamMembersFilters = {
  /** Empty means all channels. */
  channelIds: string[]
  /** Empty means all roles. */
  roles: CollaborationRole[]
  status: TeamMembersStatusFilter
}

export const EMPTY_TEAM_MEMBERS_FILTERS: TeamMembersFilters = {
  channelIds: [],
  roles: [],
  status: 'all',
}

export const TEAM_MEMBERS_STATUS_LABELS = {
  all: 'All',
  active: 'Active',
  locked: 'Locked',
  pending: 'Pending',
} satisfies Record<TeamMembersStatusFilter, string>

export const TEAM_MEMBERS_STATUSES = Object.keys(
  TEAM_MEMBERS_STATUS_LABELS,
) as TeamMembersStatusFilter[]

export const hasActiveTeamMembersFilters = (
  filters: TeamMembersFilters,
): boolean =>
  filters.channelIds.length > 0 ||
  filters.roles.length > 0 ||
  filters.status !== 'all'

/** Invitations show only when no channel, role, Active or Locked filter is set. */
export const canShowPendingInvitations = (
  filters: TeamMembersFilters,
): boolean =>
  filters.channelIds.length === 0 &&
  filters.roles.length === 0 &&
  (filters.status === 'all' || filters.status === 'pending')

/**
 * Maps the page's filters onto `TeamMembersInput.filter`, omitting what is
 * unset so an unfiltered page sends no `filter` at all.
 */
export const toTeamMembersFilterInput = ({
  channelIds,
  roles,
  status,
}: TeamMembersFilters): TeamMembersFilterInput | undefined => {
  const input: TeamMembersFilterInput = {
    ...(channelIds.length > 0 ? { channelIds } : {}),
    ...(roles.length > 0 ? { roles } : {}),
    ...(status === 'active' || status === 'locked' ? { status } : {}),
  }
  return Object.keys(input).length > 0 ? input : undefined
}
